import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { APIProvider, Map, MapCameraChangedEvent, MapEvent } from '@vis.gl/react-google-maps';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, CheckCircle, XCircle } from 'lucide-react';
import type { FirestoreLocation } from '@/types/firestore';
import { LocationListItem } from './LocationListItem';
import LocationSelectMapMarkers from '@/components/map/LocationSelectMapMarkers';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { GeoPoint } from 'firebase/firestore';
import { useTheme as useNextTheme } from 'next-themes';
import { useThemeInfo } from '@/context/ThemeContext';
import { useAppSelector } from '@/store/hooks';
import { selectMapBounds, selectMapZoom } from '@/store/slices/mapUiSlice';

const getCenterFromBounds = (bounds: google.maps.LatLngBoundsLiteral | null): { lat: number; lng: number } | null => {
    if (!bounds) return null;
    return {
        lat: (bounds.north + bounds.south) / 2,
        lng: (bounds.east + bounds.west) / 2,
    };
};

export interface LocationSelectProps {
  allLocations: FirestoreLocation[];
  selectedLocationId?: string | null;
  selectedLocationIds?: string[];
  onSelectionChange: (selectedId: string | null | string[]) => void;
  mode: 'single' | 'multi';
  containerClassName?: string;
  listMapAreaHeight?: string;
  onConfirm?: (selected: string | string[] | null) => void;
  confirmButtonText?: string;
  cancelButtonText?: string;
  onCancel?: () => void;
}

const MAP_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

const LocationSelect: React.FC<LocationSelectProps> = ({
  allLocations,
  selectedLocationId: initialSelectedLocationId,
  selectedLocationIds: initialSelectedLocationIds,
  onSelectionChange,
  mode,
  containerClassName = '',
  listMapAreaHeight = 'h-[400px]',
  onConfirm,
  confirmButtonText = 'Confirm Selection',
  cancelButtonText = 'Cancel',
  onCancel,
}) => {
  const initialMapBoundsFromStore = useAppSelector(selectMapBounds);
  const initialMapZoomFromStore = useAppSelector(selectMapZoom);

  const initialDefaultCenter = useMemo(() => {
    return getCenterFromBounds(initialMapBoundsFromStore) || { lat: 40.7128, lng: -74.0060 };
  }, [initialMapBoundsFromStore]);

  const initialDefaultZoom = useMemo(() => {
    return initialMapZoomFromStore || 5;
  }, [initialMapZoomFromStore]);

  const [searchQuery, setSearchQuery] = useState('');
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>([]);
  const [currentMapCenter, setCurrentMapCenter] = useState(initialDefaultCenter);
  const [currentMapZoom, setCurrentMapZoom] = useState(initialDefaultZoom);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { theme: activeThemeName } = useNextTheme();
  const { availableThemes } = useThemeInfo();
  const [currentMapColorScheme, setCurrentMapColorScheme] = useState<google.maps.ColorScheme | undefined>(undefined);
  const [mapKey, setMapKey] = useState<string>('initialLocationSelectMapKey');

  useEffect(() => {
    const setupMapColorScheme = async () => {
      try {
        if (!google?.maps?.importLibrary) return;
        const { ColorScheme } = await google.maps.importLibrary('core') as google.maps.CoreLibrary;
        const currentThemeDetails = availableThemes.find(t => t.value === activeThemeName);
        const isDark = currentThemeDetails?.isDark ?? false;
        const newColorSchemeValue = isDark ? ColorScheme.DARK : ColorScheme.LIGHT;
        setCurrentMapColorScheme(newColorSchemeValue);
        setMapKey(`locationSelectMap-${newColorSchemeValue.toString()}-${Date.now()}`);
      } catch (e) {
        console.error("Failed to set map colorScheme:", e);
        setMapKey(`locationSelectMap-error-${Date.now()}`);
        setCurrentMapColorScheme(undefined);
      }
    };
    if (activeThemeName) setupMapColorScheme();
  }, [activeThemeName, availableThemes]);

  useEffect(() => {
    const newSelection = mode === 'multi' ? (initialSelectedLocationIds || []) : (initialSelectedLocationId ? [initialSelectedLocationId] : []);
    setInternalSelectedIds(newSelection);

    if (mode === 'single' && newSelection.length === 1 && allLocations.length > 0) {
      const loc = allLocations.find(l => l.id === newSelection[0]);
      if (loc?.coordinates) {
        const coords = loc.coordinates as any;
        let lat: number | undefined, lng: number | undefined;
        if (coords instanceof GeoPoint) {
          lat = coords.latitude;
          lng = coords.longitude;
        } else if (coords && typeof coords.latitude === 'number' && typeof coords.longitude === 'number') {
          lat = coords.latitude;
          lng = coords.longitude;
        }
        if (lat !== undefined && lng !== undefined) {
          setCurrentMapCenter({ lat, lng });
          setCurrentMapZoom(15);
        }
      }
    }
  }, [initialSelectedLocationId, initialSelectedLocationIds, mode, allLocations]);
  
  useEffect(() => {
    if (mapInstanceRef.current && currentMapCenter) {
      if (programmaticMoveRef.current) {
        mapInstanceRef.current.panTo(currentMapCenter);
        mapInstanceRef.current.setZoom(currentMapZoom);
        programmaticMoveRef.current = false;
      }
    }
  }, [currentMapCenter, currentMapZoom]);

  const filteredLocations = useMemo(() => {
    if (!searchQuery) return allLocations;
    const lowerQuery = searchQuery.toLowerCase();
    return allLocations.filter(
      loc => loc.name.toLowerCase().includes(lowerQuery) || 
             loc.address?.toLowerCase().includes(lowerQuery)
    );
  }, [allLocations, searchQuery]);

  const handleListItemClick = useCallback((locationId: string, panToLocation = true) => {
    let newSelectedIds: string[];
    if (mode === 'single') {
      newSelectedIds = internalSelectedIds[0] === locationId ? [] : [locationId];
    } else {
      newSelectedIds = internalSelectedIds.includes(locationId)
        ? internalSelectedIds.filter(id => id !== locationId)
        : [...internalSelectedIds, locationId];
    }
    setInternalSelectedIds(newSelectedIds);
    onSelectionChange(mode === 'single' ? (newSelectedIds[0] || null) : newSelectedIds);

    if (panToLocation) {
      const loc = allLocations.find(l => l.id === locationId);
      if (loc?.coordinates) {
        const coords = loc.coordinates as any;
        let lat: number | undefined, lng: number | undefined;
         if (coords instanceof GeoPoint) {
          lat = coords.latitude;
          lng = coords.longitude;
        } else if (coords && typeof coords.latitude === 'number' && typeof coords.longitude === 'number') {
          lat = coords.latitude;
          lng = coords.longitude;
        }
        if (lat !== undefined && lng !== undefined) {
          // Set flag to indicate this is a programmatic move
          programmaticMoveRef.current = true;
          
          setCurrentMapCenter({ lat, lng });
          setCurrentMapZoom(Math.max(currentMapZoom, 15));
        }
      }
    }
  }, [mode, internalSelectedIds, onSelectionChange, allLocations, currentMapZoom]);

  const handleMapMarkerClick = useCallback((locationId: string) => {
    handleListItemClick(locationId, false); // Don't pan when clicking map marker
    const listItemElement = listRef.current?.querySelector(`#location-item-${locationId}`);
    listItemElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [handleListItemClick]);

  const handleMapClusterClick = useCallback((lng: number, lat: number, expansionZoom: number) => {
    setCurrentMapCenter({lat, lng});
    setCurrentMapZoom(expansionZoom);
  }, []);

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(mode === 'single' ? (internalSelectedIds[0] || null) : internalSelectedIds);
    }
  };
  
  const onMapIdle = useCallback((ev: MapEvent) => {
    if (ev.map) {
        mapInstanceRef.current = ev.map;
        // Only set position on first load
        if (mapInstanceRef.current && !mapInitializedRef.current && currentMapCenter) {
            mapInstanceRef.current.panTo(currentMapCenter);
            mapInstanceRef.current.setZoom(currentMapZoom);
            mapInitializedRef.current = true;
        }
    }
  }, [currentMapCenter, currentMapZoom]);

  // Add ref to track programmatic vs user-initiated changes
  const programmaticMoveRef = useRef(false);

  // Add ref to track if map has been initialized
  const mapInitializedRef = useRef(false);

  return (
    <APIProvider apiKey={MAP_API_KEY!}>
      <div className={cn("flex flex-col h-full", containerClassName)}>
        {/* Fixed height area for List and Map */}
        <div className={cn("flex flex-col md:flex-row gap-4", listMapAreaHeight)}>
          {/* List Column */}
          <div className="md:w-2/5 lg:w-1/3 h-full flex flex-col">
            <ScrollArea className="h-full border rounded-md flex-grow min-h-0" ref={listRef}>
              {filteredLocations.length > 0 ? (
                filteredLocations.map(location => (
                  <LocationListItem
                    key={location.id}
                    location={location}
                    isSelected={internalSelectedIds.includes(location.id!)}
                    onClick={handleListItemClick}
                  />
                ))
              ) : (
                <p className="p-4 text-sm text-muted-foreground text-center">No locations found.</p>
              )}
            </ScrollArea>
          </div>

          {/* Map Column */}
          <div className="md:w-3/5 lg:w-2/3 h-full">
            <div className={cn("h-full w-full rounded-md overflow-hidden border shadow-sm")}>
                <Map
                key={mapKey}
                mapId={import.meta.env.VITE_GOOGLE_MAP_ID_SELECT || import.meta.env.VITE_GOOGLE_MAP_ID}
                defaultCenter={currentMapCenter}
                defaultZoom={currentMapZoom}
                gestureHandling={'greedy'}
                disableDefaultUI={true}
                clickableIcons={false}
                onCameraChanged={(ev: MapCameraChangedEvent) => {
                    // Update state to track user's manual pan/zoom
                    setCurrentMapZoom(ev.detail.zoom); 
                    setCurrentMapCenter(ev.detail.center);
                }}
                onTilesLoaded={onMapIdle}
                colorScheme={currentMapColorScheme}
                className="h-full w-full"
                >
                {mapInstanceRef.current && (
                    <LocationSelectMapMarkers 
                        locations={filteredLocations} 
                        onMarkerClick={handleMapMarkerClick}
                        onClusterClick={handleMapClusterClick} 
                        mapBounds={mapInstanceRef.current.getBounds()?.toJSON() || null}
                        zoom={currentMapZoom}
                        selectedLocationIds={internalSelectedIds}
                    />
                )}
                </Map>
            </div>
          </div>
        </div>

        {/* Controls Area: Search and Buttons */}
        <div className="flex flex-col md:flex-row items-center gap-2 mt-3">
          {/* Search Input Wrapper - Aligns with List Column width */}
          <div className="relative w-full md:w-2/5 lg:w-1/3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9"
            />
          </div>

          {/* Buttons Wrapper - Aligns with Map Column width */}
          {(onConfirm || onCancel) && (
            <div className="flex-shrink-0 flex items-center justify-end gap-2 mt-2 md:mt-0 md:w-3/5 lg:w-2/3 w-full">
              {onCancel && (
                <Button variant="outline" onClick={onCancel} size="sm"> 
                  <XCircle className="mr-2 h-4 w-4" /> {cancelButtonText}
                </Button>
              )}
              {onConfirm && (
                <Button onClick={handleConfirm} disabled={internalSelectedIds.length === 0} size="sm"> 
                  <CheckCircle className="mr-2 h-4 w-4" /> {confirmButtonText}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </APIProvider>
  );
};

export default LocationSelect; 